import React, {
	type ClipboardEvent,
	type ElementRef,
	type FocusEvent,
	type KeyboardEvent,
	memo,
	type MouseEvent,
	type Ref,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	useImperativeHandle,
	type PropsWithChildren,
} from 'react';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import { Label } from '@atlaskit/form';
import Popup, { type TriggerProps } from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import { Flex, xcss, Box } from '@atlaskit/primitives';
import SimpleTag from '@atlaskit/tag/simple-tag';
import Tooltip from '@atlaskit/tooltip';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { CellWrapper as CellWrapperNew } from '@atlassian/jira-business-fields/src/common/cell-wrapper/index.tsx';
import { useMultiSelectKeyDown } from '@atlassian/jira-business-fields/src/common/cell-wrapper/utils.tsx';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import { getPopupComponentWithHeight } from '@atlassian/jira-business-fields/src/common/ui/popup/index.tsx';
import { TagListWrapper } from '@atlassian/jira-business-fields/src/common/ui/tag-list-wrapper/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useLabelsField } from '@atlassian/jira-issue-field-labels/src/services/labels-field-service/index.tsx';
import type { SelectOption } from '@atlassian/jira-issue-field-select/src/ui/edit/types.tsx';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { DEFAULT_ROW_HEIGHT } from '../../../common/constants.tsx';
import LazyCreatableSelect from '../../../common/ui/lazy-creatable-select/index.tsx';
import { CellContainer, CellContentWrapper } from '../../../common/ui/list-cell/styled.tsx';
import { useLabels } from '../../../controllers/labels/index.tsx';
import messages from './messages.tsx';
import type { LabelsCellProps } from './types.tsx';

const defaultIssueLabels: Array<string> = [];
const wordRegex = /(\S+)/gm;

const POPUP_OFFSET: [number, number] = [0, 1 - DEFAULT_ROW_HEIGHT];
const POPUP_OFFSET_NEW: [number, number] = [0, -DEFAULT_ROW_HEIGHT];

const LabelsCell = memo<LabelsCellProps>(
	({
		issueKey,
		issueId,
		issueLabels = defaultIssueLabels,
		width,
		fieldType,
		fieldKey,
		isSelected = false,
		wrapperRef,
		onFocus,
		onBlur,
		onKeyDown,
		isEditable,
		fieldName = '',
		issueType = '',
		issueTypeId = '',
		editMode: editModeNew = false,
		setEditMode: setEditModeNew = noop,
		isHovering: isHoveringNew = false,
		cellRef,
		inputValue,
		onInputChange,
	}: LabelsCellProps) => {
		const inputRef = useRef<HTMLInputElement>(null);
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { formatMessage } = useIntl();
		const [{ error, value }, { saveValue, resetError }] = useLabelsField({
			fieldKey,
			issueKey,
			fieldType,
			initialValue: issueLabels,
			issueId,
			onFailure: () => setLabels(issueLabels),
		});

		const { defaultSuggestions, addDefaultSuggestion, getSuggestions } = useLabels(
			issueId,
			fieldKey,
		);

		// Note the type here is not LabelsValue as it's impossible for updatedLabels to be null
		const updatedLabels: string[] = useMemo<string[]>(
			(): string[] => value || defaultIssueLabels,
			[value],
		);

		const [editMode, setEditMode] = fg('jira_list_keyboard_nav')
			? [editModeNew, setEditModeNew]
			: // eslint-disable-next-line react-hooks/rules-of-hooks
				useState<boolean>(false);
		const [isHoveringOld, setIsHovering] = useState(false);
		const isHovering = fg('jira_list_keyboard_nav') ? isHoveringNew : isHoveringOld;
		const [menuIsOpen, setMenuIsOpen] = useState(editMode);
		const wasEditMode = usePreviousWithInitial(editMode);

		useEffect(() => {
			setMenuIsOpen(editMode);
		}, [editMode]);

		// Note the type here is not LabelsValue as it's impossible for labels to be null
		const [labels, setLabels] = useState<string[]>((): string[] => updatedLabels);

		// Update the internal state in response to external data changes. Note
		// that setting state here is more efficient than with a `useEffect`
		// hook - see
		// https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
		if (
			!editMode &&
			!isEqual(labels, updatedLabels) &&
			(!fg('jira_list_keyboard_nav') || !wasEditMode)
		) {
			setLabels(updatedLabels);
		}

		const [isCreating, setIsCreating] = useState(false);
		const [shouldFocusInput, setShouldFocusInput] = useState(false);
		const [shouldSave, setShouldSave] = useState(false);

		const saveAndClose = useCallback(() => {
			setEditMode(false);
			if (!fg('jira_list_keyboard_nav')) {
				setShouldSave(false);
			}

			if (!isEqual(labels.slice().sort(), updatedLabels.slice().sort())) {
				saveValue(labels, null, createAnalyticsEvent({}))
					.then(() => {
						labels
							.filter((label: string) => issueLabels.indexOf(label) === -1)
							.forEach((suggestion) => {
								addDefaultSuggestion(suggestion);
							});
					}) // Replace with lodash/noop
					// eslint-disable-next-line @typescript-eslint/no-empty-function
					.catch(() => {});
			}

			if (!fg('jira_list_keyboard_nav')) {
				wrapperRef?.current?.focus();
			}
		}, [
			setEditMode,
			labels,
			updatedLabels,
			wrapperRef,
			saveValue,
			createAnalyticsEvent,
			issueLabels,
			addDefaultSuggestion,
		]);

		const onEscapeKeyPress = useCallback(() => {
			setEditMode(false);
			setLabels(issueLabels);
		}, [issueLabels, setEditMode]);

		const { onKeyDown: onMultiSelectKeyDown, saveOnBlur } = useMultiSelectKeyDown({
			editMode,
			wasEditMode,
			menuIsOpen,
			setMenuIsOpen,
			saveAndClose,
			onEscapeKeyPress,
		});
		useEffect(saveOnBlur, [saveOnBlur]);

		const cellOverrides = useCallback(
			() => ({ onKeyDown: onMultiSelectKeyDown }),
			[onMultiSelectKeyDown],
		);
		useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleClick = useCallback(() => {
			if (editMode === true) {
				setShouldSave(true);
			} else {
				setEditMode(true);
			}
		}, [editMode, setEditMode]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const onWrapperRef = useCallback(
			(ref: ElementRef<'div'> | null) => {
				if (wrapperRef) {
					// eslint-disable-next-line no-param-reassign
					wrapperRef.current = ref;
				}
			},
			[wrapperRef],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleKeyDown = useCallback(
			(e: KeyboardEvent<HTMLDivElement>) => {
				e.stopPropagation();
				!editMode && onKeyDown?.(e);
			},
			[editMode, onKeyDown],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleMouseEnter = useCallback(() => setIsHovering(true), [setIsHovering]);
		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleMouseLeave = useCallback(() => setIsHovering(false), [setIsHovering]);

		const handleLabelsChange = useCallback(
			(newValue: SelectOption | readonly SelectOption[] | null) => {
				resetError();
				if (newValue) {
					setLabels(
						Array.isArray(newValue)
							? newValue.map((option) => String(option.label)).sort()
							: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								[(newValue as SelectOption).label],
					);
				} else {
					setLabels([]);
				}
			},
			[resetError],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleBlur = useCallback(
			(event: FocusEvent<HTMLElement>) => {
				if (!isCreating) {
					onBlur?.(event);
				} else {
					setIsCreating(false);
					setShouldFocusInput(true);
				}
			},
			[onBlur, isCreating],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleLabelsClick = useCallback((event: MouseEvent<HTMLElement>) => {
			// block handleWrapperClick from firing and closing the popup when user clicks an option
			event.stopPropagation();
		}, []);

		const handleCreateOption = useCallback(
			(label: string) => {
				setLabels([...labels, label].sort());
				setIsCreating(true);
			},
			[labels],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in handlePopupClose
		 */
		const handleMenuClose = useCallback(() => {
			if (!isCreating) {
				setShouldSave(true);
			}
		}, [isCreating]);

		const handleInputRef = useCallback(
			(ref: Ref<'input'>) => {
				if (inputRef) {
					// @ts-expect-error - TS2540 - Cannot assign to 'current' because it is a read-only property.
					inputRef.current = ref;
				}
			},
			[inputRef],
		);

		const handlePopupClose = useCallback(() => {
			if (!fg('jira_list_keyboard_nav')) {
				setShouldSave(true);
			}
		}, []);

		const handleCutCopy = useCallback(
			(event: ClipboardEvent<HTMLElement>) => {
				event.preventDefault();
				if (Array.isArray(labels)) {
					event.clipboardData.setData('text/plain', labels.join(' '));
				}

				if (event.type === 'copy') {
					fireUIAnalytics(createAnalyticsEvent({}), 'cell copied', 'labels');
				} else if (event.type === 'cut') {
					fireUIAnalytics(createAnalyticsEvent({}), 'cell cut', 'labels');
					// clear cell content
					setLabels([]);
					saveValue(
						[],
						null,
						createAnalyticsEvent({}),
					).catch(/* ignore as it's caught by onFailure */);
				}
			},
			[labels, saveValue, createAnalyticsEvent],
		);

		const handlePaste = useCallback(
			(event: ClipboardEvent<HTMLElement>) => {
				event.preventDefault();
				const clipText = event.clipboardData.getData('text/plain');

				const words = clipText.match(wordRegex);
				if (Array.isArray(words) && words.length > 0) {
					const newLabels = Array.from(new Set([...labels, ...words]));
					setLabels(newLabels);
					saveValue(newLabels, null, createAnalyticsEvent({})).catch();
					if (fg('jira_list_keyboard_nav')) {
						setMenuIsOpen(false);
					}

					fireUIAnalytics(createAnalyticsEvent({}), 'cell pasted', 'labels', {
						valid: true,
					});
				} else {
					fireUIAnalytics(createAnalyticsEvent({}), 'cell pasted', 'labels', {
						valid: false,
						error: 'Paste content contains no words',
					});
				}
			},
			[labels, saveValue, createAnalyticsEvent],
		);

		useEffect(() => {
			if (editMode && fg('jira_list_keyboard_nav')) {
				inputRef?.current?.focus();
			}
		}, [editMode, issueLabels]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in useEffect above
		 */
		useEffect(() => {
			if (!fg('jira_list_keyboard_nav') && isSelected === false && editMode) {
				setShouldSave(true);
			}
		}, [isSelected, editMode, saveAndClose]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in useEffect above
		 */
		useEffect(() => {
			if (!fg('jira_list_keyboard_nav') && shouldFocusInput) {
				inputRef?.current?.focus();
				setShouldFocusInput(false);
			}
		}, [shouldFocusInput]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in handlePopupClose
		 */
		useEffect(() => {
			if (!fg('jira_list_keyboard_nav') && shouldSave) {
				saveAndClose();
			}
		}, [shouldSave, saveAndClose]);

		const staticContent = useMemo(
			() =>
				labels.map((label) =>
					isVisualRefreshEnabled() ? (
						<Box as="span" xcss={tagContainerStyles} key={label}>
							<SimpleTag text={label} />
						</Box>
					) : (
						<SimpleTag key={label} text={label} />
					),
				),
			[labels],
		);

		const defaultOptions = useMemo(
			() =>
				defaultSuggestions
					.filter((suggestion) => labels.indexOf(suggestion.label) === -1)
					.map((suggestion) => ({
						label: suggestion.label,
						value: suggestion.label,
					})),
			[defaultSuggestions, labels],
		);

		const styles = useMemo(
			() => ({
				option: (base?: Record<string, string>) => ({
					...base,
					textOverflow: 'ellipsis',
					overflow: 'hidden',
				}),
			}),
			[],
		);

		const formatCreateLabel = useCallback(
			(inputValue_: string): string => `${inputValue_} (${formatMessage(messages.createNewItem)})`,
			[formatMessage],
		);

		const noOptionsMessage = useCallback(
			(args: { inputValue: string }) =>
				labels.find((label) => label === args.inputValue)
					? formatMessage(messages.duplicate)
					: formatMessage(messages.noResults),
			[formatMessage, labels],
		);

		const popupTrigger = useCallback(
			(triggerProps: TriggerProps) => (
				<div {...triggerProps}>
					<TagListWrapper
						isEditable={isEditable}
						data-testid="business-list.ui.list-view.labels-cell.tag-list-wrapper"
					>
						<Tooltip content={labels.join(', ')} position="top">
							{staticContent}
						</Tooltip>
					</TagListWrapper>
				</div>
			),
			[labels, isEditable, staticContent],
		);

		const popupContent = useCallback(
			() => (
				<CellContentWrapper
					preferredWidth={width - 1}
					onClick={fg('jira_list_keyboard_nav') ? noop : handleLabelsClick}
				>
					<VisuallyHidden>
						<Label htmlFor="business-list.ui.list-view.labels-cell.popup-content-lazy-creatable-select">
							{formatMessage(messages.labelMultipleOptionsLabel)}
						</Label>
					</VisuallyHidden>
					<LazyCreatableSelect
						isMulti
						isClearable
						autoFocus
						menuIsOpen={menuIsOpen}
						placeholder={formatMessage(messages.placeholder)}
						value={labels.map((label) => ({ value: label, label }))}
						onChange={handleLabelsChange}
						loadOptions={getSuggestions}
						defaultOptions={defaultOptions}
						onCreateOption={handleCreateOption}
						formatCreateLabel={formatCreateLabel}
						noOptionsMessage={noOptionsMessage}
						onMenuClose={fg('jira_list_keyboard_nav') ? noop : handleMenuClose}
						closeMenuOnSelect={false}
						onInputRef={handleInputRef}
						inputId="business-list.ui.list-view.labels-cell.popup-content-lazy-creatable-select"
						label={formatMessage(messages.selectBoxLabel)}
						tabSelectsValue={!!fg('jira_list_keyboard_nav')}
						inputValue={fg('jira_list_keyboard_nav') ? inputValue : undefined}
						onInputChange={fg('jira_list_keyboard_nav') ? onInputChange : undefined}
						{...(isVisualRefreshEnabled() && fg('visual-refresh_drop_3') ? { styles } : {})}
					/>
				</CellContentWrapper>
			),
			[
				width,
				handleLabelsClick,
				formatMessage,
				menuIsOpen,
				labels,
				handleLabelsChange,
				getSuggestions,
				defaultOptions,
				handleCreateOption,
				formatCreateLabel,
				noOptionsMessage,
				handleMenuClose,
				handleInputRef,
				inputValue,
				onInputChange,
				styles,
			],
		);

		const popupComponent = useMemo(
			() =>
				getPopupComponentWithHeight({
					numOptions: Math.min(8, defaultSuggestions.length),
					hasFooter: true,
				}),
			[defaultSuggestions],
		);

		const cellContent = (
			<>
				{isHovering || editMode || isSelected ? (
					<Popup
						isOpen={editMode}
						placement="bottom-start"
						autoFocus={false}
						offset={fg('jira_list_keyboard_nav') ? POPUP_OFFSET_NEW : POPUP_OFFSET}
						onClose={handlePopupClose}
						popupComponent={popupComponent}
						content={popupContent}
						trigger={popupTrigger}
						testId="business-list.ui.list-view.labels-cell.labels-menu"
					/>
				) : (
					<TagListWrapper>{staticContent}</TagListWrapper>
				)}
				{error && (
					<FixItErrorFlag
						error={error}
						title={messages.errorTitle}
						description={messages.errorMessage}
						issueType={issueType}
						issueTypeId={issueTypeId}
						fieldId={fieldKey}
						fieldName={fieldName}
					/>
				)}
			</>
		);

		if (fg('jira_list_keyboard_nav')) {
			return (
				<CellWrapperNew
					isEditable={isEditable}
					testId="business-list.ui.list-view.labels-cell.cell-container"
					hasPadding={false}
					{...(!fg('jwm_list_new_copy_paste_behaviour')
						? {
								onCopy: handleCutCopy,
								onCut: handleCutCopy,
								onPaste: handlePaste,
							}
						: {})}
				>
					<ContentWrapper>{cellContent}</ContentWrapper>
				</CellWrapperNew>
			);
		}

		return (
			<CellContainer
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onCopy={handleCutCopy}
				onCut={handleCutCopy}
				onPaste={handlePaste}
				onBlur={handleBlur}
				tabIndex={0}
				onFocus={onFocus}
				onKeyDown={handleKeyDown}
				ref={onWrapperRef}
				onClick={handleClick}
				isFocused={isSelected && !editMode}
				data-testid="business-list.ui.list-view.labels-cell.cell-container"
			>
				{cellContent}
			</CellContainer>
		);
	},
);

export default LabelsCell;

const ContentWrapper = ({ children }: PropsWithChildren) => (
	<Flex xcss={[wrapperStyles]}>{children}</Flex>
);

const wrapperStyles = xcss({
	width: '100%',
	height: '100%',
});

const tagContainerStyles = xcss({
	display: 'inline-flex',
	minWidth: '45px',
});
